import { useTerminalsStateRE } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import {
  OPTION_ALL,
  hasNonAllOptionActive,
} from "@/components/pageCards/filterSort/constants";
import {
  createTimeFilters,
  getTenantCategoryOptions,
} from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import {
  FilterSort,
  FilterSortMenuType,
} from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";

import { useMemo, useState } from "react";

import moment from "moment-timezone";
import { FilterTimeFrameValue } from "server/config-api/index.js";

export const usePerformance = () => {
  const [
    { timeframe: [selectedTimeframe] = [], tenantCategory },
    setActiveOptions,
  ] = useState<ActiveOptions<"timeframe" | "tenantCategory">>({} as any);

  const { timezone } = timezoneStore();
  const { terminalConfig } = useConfigService();
  const defaultPeriod = useMemo(
    () => terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.Last30Days,
    [terminalConfig],
  );
  const { startTimeISO, endTimeISO } = useMemo(() => {
    return getTimeFrame(
      selectedTimeframe?.value as FilterTimeFrameValue,
      defaultPeriod,
    );
  }, [selectedTimeframe, defaultPeriod]);

  // The start of the comparison period (an equal length period ending when the current period starts)
  const firstPeriodStartTimeISO = useMemo(() => {
    return moment(startTimeISO)
      .subtract(moment(endTimeISO).diff(moment(startTimeISO), "days"), "days")
      .tz(timezone)
      .toISOString();
  }, [startTimeISO, endTimeISO, timezone]);

  const { currentTerminal } = useTerminalsStateRE();
  const { usedTenantCategories } = useMinimalCustomers();

  const filters: FilterSort[] = [
    createTimeFilters({ id: "timeframe", defaultValue: defaultPeriod }, [
      FilterTimeFrameValue.Last30Days,
      FilterTimeFrameValue.Last6FullMonths,
      FilterTimeFrameValue.LastFullYear,
    ]),
    {
      options: getTenantCategoryOptions(usedTenantCategories),
      title: "statisticsLabels:tenantCategory",
      id: "tenantCategory",
      type: "filter",
      menuType: FilterSortMenuType.Select,
      defaultValue: OPTION_ALL,
    },
  ];

  const tenantCategoryValue = useMemo(() => {
    return hasNonAllOptionActive(tenantCategory)
      ? tenantCategory[0].value
      : undefined;
  }, [tenantCategory]);

  const {
    data: firstPeriod,
    isLoading: isFirstPeriodLoading,
    isError: isFirstPeriodError,
  } = trpc.dwh.customers.get.useQuery(
    {
      startTimeISO: firstPeriodStartTimeISO,
      endTimeISO: startTimeISO,
      commercialCustomerCategory: tenantCategoryValue,
      terminalId: currentTerminal.id,
    },
    {
      queryKeyHashFn: () =>
        [
          "customersFirstPeriod",
          startTimeISO,
          endTimeISO,
          tenantCategory,
          currentTerminal.id,
        ].toString(),
    },
  );

  const { data: accessCodes, isLoading: isLoadingAccessCodes } =
    trpc.scanner.customers.getAllAccessCodes.useQuery(
      {
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          ["customersAccessCodes", currentTerminal].toString(),
      },
    );

  const {
    data: currentPeriod,
    isLoading: isCurrentPeriodLoading,
    isError: isCurrentPeriodError,
  } = trpc.dwh.customers.get.useQuery(
    {
      startTimeISO,
      endTimeISO,
      commercialCustomerCategory: tenantCategoryValue,
      terminalId: currentTerminal.id,
    },
    {
      queryKeyHashFn: () =>
        [
          "customers",
          startTimeISO,
          endTimeISO,
          tenantCategory,
          currentTerminal.id,
        ].toString(),
    },
  );

  const isLoading =
    isFirstPeriodLoading || isCurrentPeriodLoading || isLoadingAccessCodes;
  const isError = isFirstPeriodError || isCurrentPeriodError;

  return {
    filters,
    firstPeriod,
    currentPeriod,
    isLoading,
    isError,
    setActiveOptions,
    accessCodes,
  };
};
